import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Camera, Pencil, Trash2, Package, User, Calendar, RotateCcw, TrendingUp, MapPin, UserPlus, UserCircle } from 'lucide-react';
import { ConfirmDialog } from '@/components/ConfirmDialog';

const Rentals = () => {
  const { user } = useContext(AuthContext);
  const [equipment, setEquipment] = useState([]);
  const [activeRentals, setActiveRentals] = useState([]);
  const [myRentals, setMyRentals] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [borrowDialogOpen, setBorrowDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmReturnOpen, setConfirmReturnOpen] = useState(false);
  const [deleteEquipId, setDeleteEquipId] = useState(null);
  const [returnRentalId, setReturnRentalId] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [stockAdjustment, setStockAdjustment] = useState('');
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    photo: '',
    quantity_total: 1,
    storage_location: '',
  });
  const [borrowFormData, setBorrowFormData] = useState({
    quantity: 1,
    expected_return_date: '',
  });
  const [assignFormData, setAssignFormData] = useState({
    collaborator_id: '',
    quantity: 1,
    expected_return_date: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const filtered = equipment.filter((equip) =>
      equip.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (equip.description && equip.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredEquipment(filtered);
  }, [searchTerm, equipment]);

  const loadData = async () => {
    try {
      const requests = [
        axios.get(`${API}/rentals/equipment`),
        axios.get(`${API}/rentals/records`),
        axios.get(`${API}/rentals/my-rentals`)
      ];
      
      // Load collaborators only for admin
      if (user?.role === 'admin') {
        requests.push(axios.get(`${API}/collaborators`));
      }
      
      const results = await Promise.all(requests);
      setEquipment(results[0].data);
      setFilteredEquipment(results[0].data);
      setActiveRentals(results[1].data);
      setMyRentals(results[2].data);
      
      if (user?.role === 'admin' && results[3]) {
        setCollaborators(results[3].data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const { data } = await axios.post(`${API}/rentals/upload-photo`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData({ ...formData, photo: data.photo });
      toast.success('Photo ajoutée avec succès');
    } catch (error) {
      toast.error('Erreur lors du téléchargement de la photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const updateData = {
          name: formData.name,
          description: formData.description || null,
          photo: formData.photo || null,
          storage_location: formData.storage_location || null,
        };
        await axios.put(`${API}/rentals/equipment/${editingId}`, updateData);
        toast.success('Équipement modifié avec succès');
      } else {
        const createData = {
          ...formData,
          description: formData.description || null,
          photo: formData.photo || null,
          storage_location: formData.storage_location || null,
        };
        await axios.post(`${API}/rentals/equipment`, createData);
        toast.success('Équipement ajouté avec succès');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const handleBorrow = async (e) => {
    e.preventDefault();
    if (!selectedEquipment) return;
    
    try {
      await axios.post(`${API}/rentals/borrow`, {
        equipment_id: selectedEquipment.id,
        quantity: parseInt(borrowFormData.quantity),
        expected_return_date: borrowFormData.expected_return_date || null,
      });
      toast.success('Équipement emprunté avec succès');
      setBorrowDialogOpen(false);
      resetBorrowForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const handleReturn = async (rentalId) => {
    setReturnRentalId(rentalId);
    setConfirmReturnOpen(true);
  };

  const confirmReturn = async () => {
    if (!returnRentalId) return;
    try {
      await axios.post(`${API}/rentals/return/${returnRentalId}`);
      toast.success('Équipement retourné avec succès');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du retour');
    } finally {
      setConfirmReturnOpen(false);
      setReturnRentalId(null);
    }
  };

  const handleDelete = async (id) => {
    setDeleteEquipId(id);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteEquipId) return;
    try {
      await axios.delete(`${API}/rentals/equipment/${deleteEquipId}`);
      toast.success('Équipement supprimé avec succès');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setConfirmDeleteOpen(false);
      setDeleteEquipId(null);
    }
  };

  const handleAdjustStock = async () => {
    if (!stockAdjustment || stockAdjustment === '0') {
      toast.error('Veuillez entrer une quantité');
      return;
    }

    try {
      await axios.post(`${API}/rentals/equipment/${selectedEquipment.id}/adjust-stock`, {
        quantity_change: parseInt(stockAdjustment)
      });
      toast.success('Stock ajusté avec succès');
      setAdjustDialogOpen(false);
      setStockAdjustment('');
      setSelectedEquipment(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajustement');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      photo: '',
      quantity_total: 1,
      storage_location: '',
    });
    setEditingId(null);
  };

  const resetBorrowForm = () => {
    setBorrowFormData({
      quantity: 1,
      expected_return_date: '',
    });
    setSelectedEquipment(null);
  };

  const resetAssignForm = () => {
    setAssignFormData({
      collaborator_id: '',
      quantity: 1,
      expected_return_date: '',
    });
    setSelectedEquipment(null);
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!selectedEquipment || !assignFormData.collaborator_id) return;
    
    try {
      await axios.post(`${API}/rentals/assign`, {
        equipment_id: selectedEquipment.id,
        collaborator_id: assignFormData.collaborator_id,
        quantity: parseInt(assignFormData.quantity),
        expected_return_date: assignFormData.expected_return_date || null,
      });
      toast.success('Équipement assigné avec succès');
      setAssignDialogOpen(false);
      resetAssignForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const openAssignDialog = (equip) => {
    setSelectedEquipment(equip);
    setAssignDialogOpen(true);
  };

  const openEditDialog = (equip) => {
    setFormData({
      name: equip.name,
      description: equip.description || '',
      photo: equip.photo || '',
      quantity_total: equip.quantity_total,
      storage_location: equip.storage_location || '',
    });
    setEditingId(equip.id);
    setDialogOpen(true);
  };

  const openBorrowDialog = (equip) => {
    setSelectedEquipment(equip);
    setBorrowDialogOpen(true);
  };

  const openAdjustDialog = (equip, e) => {
    e.stopPropagation();
    setSelectedEquipment(equip);
    setAdjustDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight leading-none">
            Location
          </h1>
          <p className="text-muted-foreground mt-2">
            Matériel partagé - Empruntez et retournez des équipements
          </p>
        </div>
        {user?.role === 'admin' && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-rental-equipment-button"
                className="rounded-full shadow-lg shadow-primary/20"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un équipement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingId ? 'Modifier' : 'Nouvel'} Équipement
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Photo</Label>
                  <div className="flex flex-col gap-3">
                    {formData.photo && (
                      <img
                        src={formData.photo}
                        alt="Aperçu"
                        className="w-full h-48 object-cover rounded-lg border"
                      />
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="rounded-full"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      {uploadingPhoto ? 'Traitement...' : 'Prendre une photo'}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Nom *</Label>
                  <Input
                    id="name"
                    data-testid="rental-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="rounded-lg"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    data-testid="rental-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="rounded-lg"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="storage_location">Emplacement de retour / Lieu de stockage</Label>
                  <Input
                    id="storage_location"
                    data-testid="rental-storage-location-input"
                    value={formData.storage_location}
                    onChange={(e) => setFormData({ ...formData, storage_location: e.target.value })}
                    placeholder="Ex: Entrepôt A, Étagère 3"
                    className="rounded-lg"
                  />
                </div>

                {!editingId && (
                  <div className="space-y-2">
                    <Label htmlFor="quantity_total">Quantité initiale *</Label>
                    <Input
                      id="quantity_total"
                      data-testid="rental-quantity-input"
                      type="number"
                      min="1"
                      value={formData.quantity_total}
                      onChange={(e) => setFormData({ ...formData, quantity_total: parseInt(e.target.value) })}
                      required
                      className="rounded-lg"
                    />
                  </div>
                )}

                <Button
                  data-testid="rental-submit-button"
                  type="submit"
                  className="w-full rounded-full"
                >
                  {editingId ? 'Modifier' : 'Ajouter'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs defaultValue="equipment" className="space-y-6">
        <TabsList className={`grid w-full max-w-xl ${user?.role === 'admin' ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <TabsTrigger value="equipment" data-testid="tab-equipment">Équipements</TabsTrigger>
          <TabsTrigger value="my-rentals" data-testid="tab-my-rentals">
            <UserCircle className="w-4 h-4 mr-2" />
            Mes locations ({myRentals.length})
          </TabsTrigger>
          {user?.role === 'admin' && (
            <TabsTrigger value="rentals" data-testid="tab-rentals">
              Toutes ({activeRentals.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="equipment" className="space-y-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="rental-search-input"
              placeholder="Rechercher un équipement..."
              className="pl-10 rounded-lg max-w-md"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredEquipment.map((equip) => {
              const borrowed = equip.quantity_total - equip.quantity_available;
              return (
                <Card
                  key={equip.id}
                  data-testid={`rental-card-${equip.id}`}
                  className="hover:shadow-lg transition-shadow duration-200 overflow-hidden"
                >
                  {equip.photo ? (
                    <div className="w-full h-48 bg-muted/30 flex items-center justify-center p-2">
                      <img
                        src={equip.photo}
                        alt={equip.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-48 bg-muted flex items-center justify-center">
                      <Package className="w-12 h-12 text-muted-foreground" />
                    </div>
                  )}
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-heading">{equip.name}</CardTitle>
                    {equip.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{equip.description}</p>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {equip.storage_location && (
                      <div className="flex items-center gap-2 text-sm bg-muted/50 p-2 rounded-lg">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Retour:</span>
                        <span className="font-medium">{equip.storage_location}</span>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Total:</span>
                        <Badge variant="outline">{equip.quantity_total}</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Disponibles:</span>
                        <Badge variant="default" className="bg-chart-3">
                          {equip.quantity_available}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Empruntés:</span>
                        <Badge variant="secondary">{borrowed}</Badge>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2">
                      {equip.quantity_available > 0 && (
                        <Button
                          data-testid={`borrow-${equip.id}`}
                          onClick={() => openBorrowDialog(equip)}
                          variant="default"
                          size="sm"
                          className="flex-1 rounded-full"
                        >
                          Emprunter
                        </Button>
                      )}
                      {user?.role === 'admin' && equip.quantity_available > 0 && (
                        <Button
                          data-testid={`assign-rental-${equip.id}`}
                          onClick={() => openAssignDialog(equip)}
                          variant="secondary"
                          size="sm"
                          className="rounded-full"
                          title="Assigner à un collaborateur"
                        >
                          <UserPlus className="w-3 h-3 mr-1" />
                          Assigner
                        </Button>
                      )}
                      {user?.role === 'admin' && (
                        <>
                          <Button
                            data-testid={`adjust-rental-stock-${equip.id}`}
                            onClick={(e) => openAdjustDialog(equip, e)}
                            variant="outline"
                            size="sm"
                            className="rounded-full"
                          >
                            <TrendingUp className="w-3 h-3" />
                          </Button>
                          <Button
                            data-testid={`edit-rental-${equip.id}`}
                            onClick={() => openEditDialog(equip)}
                            variant="outline"
                            size="sm"
                            className="rounded-full"
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            data-testid={`delete-rental-${equip.id}`}
                            onClick={() => handleDelete(equip.id)}
                            variant="destructive"
                            size="sm"
                            className="rounded-full"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {filteredEquipment.length === 0 && (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucun équipement de location trouvé</p>
            </div>
          )}
        </TabsContent>

        {/* My Rentals Tab */}
        <TabsContent value="my-rentals" className="space-y-4">
          <h2 className="text-2xl font-heading font-bold">Mes équipements en location</h2>
          
          {myRentals.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myRentals.map((rental) => (
                <Card
                  key={rental.id}
                  data-testid={`my-rental-${rental.id}`}
                  className="hover:shadow-lg transition-shadow duration-200"
                >
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      {rental.equipment?.photo ? (
                        <img
                          src={rental.equipment.photo}
                          alt={rental.equipment.name}
                          className="w-12 h-12 object-contain bg-muted rounded-lg"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                          <Package className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <CardTitle className="text-lg font-heading flex items-center gap-2">
                          {rental.equipment?.name}
                          <Badge variant="secondary">x{rental.quantity}</Badge>
                        </CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>Emprunté le: {formatDate(rental.borrowed_date)}</span>
                    </div>
                    {rental.expected_return_date && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <span>Retour prévu: {formatDate(rental.expected_return_date)}</span>
                      </div>
                    )}
                    <Button
                      data-testid={`return-my-rental-${rental.id}`}
                      onClick={() => handleReturn(rental.id)}
                      variant="outline"
                      size="sm"
                      className="w-full rounded-full mt-2"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Retourner
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-2 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Package className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Vous n'avez aucun équipement en location</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {user?.role === 'admin' && (
        <TabsContent value="rentals" className="space-y-4">
          <h2 className="text-2xl font-heading font-bold">Équipements actuellement empruntés</h2>
          
          {activeRentals.length > 0 ? (
            (() => {
              // Group by borrower (collaborator or user)
              const groups = activeRentals.reduce((acc, r) => {
                const key = r.collaborator_id || r.borrower_user_id || r.borrower_name || 'unknown';
                if (!acc[key]) {
                  acc[key] = {
                    borrower_name: r.borrower_name,
                    borrower_user_id: r.borrower_user_id,
                    collaborator_id: r.collaborator_id,
                    rentals: [],
                  };
                }
                acc[key].rentals.push(r);
                return acc;
              }, {});
              const sortedGroups = Object.entries(groups).sort(
                ([, a], [, b]) => b.rentals.length - a.rentals.length
              );
              
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sortedGroups.map(([key, group]) => {
                    if (group.rentals.length === 1) {
                      const rental = group.rentals[0];
                      return (
                        <Card
                          key={rental.id}
                          data-testid={`active-rental-${rental.id}`}
                          className="hover:shadow-lg transition-shadow duration-200"
                        >
                          <CardHeader>
                            <div className="flex items-center gap-3">
                              {rental.equipment?.photo ? (
                                <img src={rental.equipment.photo} alt={rental.equipment.name} className="w-12 h-12 object-contain bg-muted rounded-lg" />
                              ) : (
                                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                                  <Package className="w-6 h-6 text-muted-foreground" />
                                </div>
                              )}
                              <div>
                                <CardTitle className="text-lg font-heading flex items-center gap-2">
                                  {rental.equipment?.name}
                                  <Badge variant="secondary">x{rental.quantity}</Badge>
                                </CardTitle>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="flex items-center gap-2 text-sm">
                              <User className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">{rental.borrower_name}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Calendar className="w-4 h-4" />
                              <span>Emprunté le: {formatDate(rental.borrowed_date)}</span>
                            </div>
                            {rental.expected_return_date && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Calendar className="w-4 h-4" />
                                <span>Retour prévu: {formatDate(rental.expected_return_date)}</span>
                              </div>
                            )}
                            {(rental.borrower_user_id === user?.id || user?.role === 'admin') && (
                              <Button
                                data-testid={`return-rental-${rental.id}`}
                                onClick={() => handleReturn(rental.id)}
                                variant="outline"
                                size="sm"
                                className="w-full rounded-full mt-2"
                              >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Retourner
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      );
                    }
                    
                    // Grouped vignette
                    return (
                      <Card
                        key={key}
                        data-testid={`active-rental-group-${key}`}
                        className="hover:shadow-lg transition-shadow duration-200 border-2 border-primary/30 bg-primary/5 md:col-span-2 lg:col-span-1"
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <User className="w-5 h-5 text-primary shrink-0" />
                              <div className="min-w-0">
                                <CardTitle className="text-lg font-heading truncate">
                                  {group.borrower_name}
                                </CardTitle>
                                <p className="text-xs text-muted-foreground">
                                  Locations groupées
                                </p>
                              </div>
                            </div>
                            <Badge variant="default" className="shrink-0">
                              {group.rentals.length} équip.
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {group.rentals
                            .slice()
                            .sort((a, b) => (b.borrowed_date || '').localeCompare(a.borrowed_date || ''))
                            .map((rental) => (
                              <div
                                key={rental.id}
                                data-testid={`grouped-rental-${rental.id}`}
                                className="p-3 bg-white rounded-lg border border-primary/10 space-y-2"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    {rental.equipment?.photo ? (
                                      <img src={rental.equipment.photo} alt={rental.equipment.name} className="w-10 h-10 object-contain bg-muted rounded shrink-0" />
                                    ) : (
                                      <div className="w-10 h-10 bg-muted rounded flex items-center justify-center shrink-0">
                                        <Package className="w-5 h-5 text-muted-foreground" />
                                      </div>
                                    )}
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium truncate">{rental.equipment?.name}</span>
                                        <Badge variant="secondary" className="text-[10px]">x{rental.quantity}</Badge>
                                      </div>
                                      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {formatDate(rental.borrowed_date)}
                                        {rental.expected_return_date && ` → ${formatDate(rental.expected_return_date)}`}
                                      </p>
                                    </div>
                                  </div>
                                  <Button
                                    data-testid={`return-rental-${rental.id}`}
                                    onClick={() => handleReturn(rental.id)}
                                    variant="outline"
                                    size="sm"
                                    className="h-7 px-3 text-xs rounded-full shrink-0"
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    Retourner
                                  </Button>
                                </div>
                              </div>
                            ))}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            <Card className="border-2 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Package className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Aucun équipement n'est actuellement emprunté</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        )}
      </Tabs>

      {/* Borrow Dialog */}
      <Dialog open={borrowDialogOpen} onOpenChange={(open) => {
        setBorrowDialogOpen(open);
        if (!open) resetBorrowForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Emprunter un équipement</DialogTitle>
          </DialogHeader>
          {selectedEquipment && (
            <form onSubmit={handleBorrow} className="space-y-4">
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <p className="text-lg font-heading font-medium">{selectedEquipment.name}</p>
                {selectedEquipment.description && (
                  <p className="text-sm text-muted-foreground">{selectedEquipment.description}</p>
                )}
                <p className="text-sm">
                  <span className="text-muted-foreground">Disponible:</span>{' '}
                  <Badge variant="default" className="bg-chart-3">{selectedEquipment.quantity_available}</Badge>
                </p>
                {selectedEquipment.storage_location && (
                  <div className="flex items-center gap-2 text-sm pt-2 border-t mt-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span className="text-muted-foreground">À retourner à:</span>
                    <span className="font-medium text-primary">{selectedEquipment.storage_location}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="borrow_quantity">Quantité *</Label>
                <Input
                  id="borrow_quantity"
                  data-testid="borrow-quantity-input"
                  type="number"
                  min="1"
                  max={selectedEquipment.quantity_available}
                  value={borrowFormData.quantity}
                  onChange={(e) => setBorrowFormData({ ...borrowFormData, quantity: e.target.value })}
                  required
                  className="rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expected_return">Date de retour prévue</Label>
                <Input
                  id="expected_return"
                  data-testid="borrow-return-date-input"
                  type="date"
                  value={borrowFormData.expected_return_date}
                  onChange={(e) => setBorrowFormData({ ...borrowFormData, expected_return_date: e.target.value })}
                  className="rounded-lg"
                />
              </div>

              <Button
                data-testid="confirm-borrow-button"
                type="submit"
                className="w-full rounded-full"
              >
                Confirmer l'emprunt
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign to Collaborator Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={(open) => {
        setAssignDialogOpen(open);
        if (!open) resetAssignForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Assigner à un collaborateur</DialogTitle>
          </DialogHeader>
          {selectedEquipment && (
            <form onSubmit={handleAssign} className="space-y-4">
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <p className="text-lg font-heading font-medium">{selectedEquipment.name}</p>
                {selectedEquipment.description && (
                  <p className="text-sm text-muted-foreground">{selectedEquipment.description}</p>
                )}
                <p className="text-sm">
                  <span className="text-muted-foreground">Disponible:</span>{' '}
                  <Badge variant="default" className="bg-chart-3">{selectedEquipment.quantity_available}</Badge>
                </p>
                {selectedEquipment.storage_location && (
                  <div className="flex items-center gap-2 text-sm pt-2 border-t mt-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span className="text-muted-foreground">À retourner à:</span>
                    <span className="font-medium text-primary">{selectedEquipment.storage_location}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="collaborator_id">Collaborateur *</Label>
                <Select
                  value={assignFormData.collaborator_id}
                  onValueChange={(value) => setAssignFormData({ ...assignFormData, collaborator_id: value })}
                  required
                >
                  <SelectTrigger id="collaborator_id" data-testid="assign-collaborator-select" className="rounded-lg">
                    <SelectValue placeholder="Sélectionner un collaborateur" />
                  </SelectTrigger>
                  <SelectContent>
                    {collaborators.map((collab) => (
                      <SelectItem key={collab.id} value={collab.id}>
                        {collab.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="assign_quantity">Quantité *</Label>
                <Input
                  id="assign_quantity"
                  data-testid="assign-quantity-input"
                  type="number"
                  min="1"
                  max={selectedEquipment.quantity_available}
                  value={assignFormData.quantity}
                  onChange={(e) => setAssignFormData({ ...assignFormData, quantity: e.target.value })}
                  required
                  className="rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="assign_expected_return">Date de retour prévue</Label>
                <Input
                  id="assign_expected_return"
                  data-testid="assign-return-date-input"
                  type="date"
                  value={assignFormData.expected_return_date}
                  onChange={(e) => setAssignFormData({ ...assignFormData, expected_return_date: e.target.value })}
                  className="rounded-lg"
                />
              </div>

              <Button
                data-testid="confirm-assign-button"
                type="submit"
                className="w-full rounded-full"
                disabled={!assignFormData.collaborator_id}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Confirmer l'assignation
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={(open) => {
        setAdjustDialogOpen(open);
        if (!open) {
          setStockAdjustment('');
          setSelectedEquipment(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Ajuster le Stock</DialogTitle>
          </DialogHeader>
          {selectedEquipment && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <p className="text-lg font-heading font-medium">{selectedEquipment.name}</p>
                <div className="grid grid-cols-3 gap-4 pt-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="text-xl font-bold">{selectedEquipment.quantity_total}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Disponible</p>
                    <p className="text-xl font-bold text-chart-3">{selectedEquipment.quantity_available}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Emprunté</p>
                    <p className="text-xl font-bold">{selectedEquipment.quantity_total - selectedEquipment.quantity_available}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="adjustment">Ajustement de quantité</Label>
                <Input
                  id="adjustment"
                  data-testid="rental-stock-adjustment-input"
                  type="number"
                  placeholder="Ex: +5 ou -3"
                  value={stockAdjustment}
                  onChange={(e) => setStockAdjustment(e.target.value)}
                  className="rounded-lg"
                />
                <p className="text-xs text-muted-foreground">
                  Entrez un nombre positif pour ajouter au stock, négatif pour retirer
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => setAdjustDialogOpen(false)}
                  variant="outline"
                  className="flex-1 rounded-full"
                >
                  Annuler
                </Button>
                <Button
                  data-testid="confirm-rental-adjustment-button"
                  onClick={handleAdjustStock}
                  className="flex-1 rounded-full"
                >
                  Confirmer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        onConfirm={confirmDelete}
        title="Supprimer l'équipement"
        description="Êtes-vous sûr de vouloir supprimer cet équipement de location ? Cette action est irréversible."
      />

      <ConfirmDialog
        open={confirmReturnOpen}
        onOpenChange={setConfirmReturnOpen}
        onConfirm={confirmReturn}
        title="Retourner l'équipement"
        description="Confirmez-vous le retour de cet équipement ? Il sera de nouveau disponible pour les autres utilisateurs."
      />
    </div>
  );
};

export default Rentals;
